import type { JSX } from "react";
import type React from "react";
import type { Season } from "./qy/season";

/* 四季版画：木刻线描，只用纸、墨、黄土三色（薄日轮用朱砂）。
   视窗 1600×480，xMidYMax slice 铺满版头，窄屏裁两侧、保底边。三层景深：
   远（塬的台地线、远树远丘）、中（母题）、近（草丛、篱笆、沙柳）。
   春 榆钱新枝 · 夏 镇北台与长城祥云 · 秋 沙脊与雁阵 · 冬 塬上窑洞与落雪。
   线宽以 CSS 像素计（vector-effect: non-scaling-stroke）；路径 pathLength=1 供描线动画。纯装饰，aria-hidden。 */

export type Point = readonly [number, number];
export const W = 1600;
export const H = 480;
export const PAPER = "var(--paper)";
export const INK = "var(--ink)";
export const LOESS = "var(--loess)";
export const P = (props: React.SVGProps<SVGPathElement>): JSX.Element => <path pathLength={1} {...props} />;

const f = (n: number) => n.toFixed(1);
/** Catmull-Rom 转三次贝塞尔，采样点连成顺滑曲线。 */
export function smooth(points: readonly Point[]): string {
  if (points.length < 2) return "";
  let d = `M${f(points[0][0])} ${f(points[0][1])}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)], p1 = points[i], p2 = points[i + 1], p3 = points[Math.min(points.length - 1, i + 2)];
    d += ` C${f(p1[0] + (p2[0] - p0[0]) / 6)} ${f(p1[1] + (p2[1] - p0[1]) / 6)} ${f(p2[0] - (p3[0] - p1[0]) / 6)} ${f(p2[1] - (p3[1] - p1[1]) / 6)} ${f(p2[0])} ${f(p2[1])}`;
  }
  return d;
}
/** 闭合到画面底边，用纸色把后面的线遮住，前后层次才分得开。 */
export const massBelow = (points: readonly Point[]) => `${smooth(points)} L${f(points[points.length - 1][0])} ${H} L${f(points[0][0])} ${H} Z`;

/** 塬的台地线：两个谐波叠加，逐线相位不同，像一层层的梯田。 */
function strataLines(count: number, base: number, spacing: number): string[] {
  return Array.from({ length: count }, (_, i) => {
    const phase = i * 1.7, amp = 8 + (i % 3) * 4;
    const points: Point[] = [];
    for (let x = -30; x <= W + 30; x += 28) points.push([x, base - i * spacing + amp * Math.sin(x / 230 + phase) + amp * 0.5 * Math.sin(x / 90 + phase * 2.3) + i * 2.5 * Math.sin(x / 420 + i)]);
    return smooth(points);
  });
}
const STRATA = strataLines(7, 468, 24);
export function Strata({ from = 0, to = STRATA.length }: { from?: number; to?: number }): JSX.Element {
  return <g data-layer="base" fill="none" stroke={LOESS} strokeLinecap="round">{STRATA.slice(from, to).map((d, index) => <P key={d} d={d} strokeWidth={index < 2 ? 1.2 : 1} opacity={0.85 - (index + from) * 0.1} />)}</g>;
}
export function Sun({ cx, cy, r, opacity = 0.55 }: { cx: number; cy: number; r: number; opacity?: number }): JSX.Element {
  return <g opacity={opacity}><circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--cinnabar)" strokeWidth="1" /></g>;
}
/** 草丛：从一点散开的几笔。 */
export function Tuft({ x, y, size = 1, tone = INK }: { x: number; y: number; size?: number; tone?: string }): JSX.Element {
  const s = size;
  return <g fill="none" stroke={tone} strokeWidth="1" strokeLinecap="round" opacity=".8">
    <P d={`M${x} ${y} q ${-6 * s} ${-20 * s} ${-16 * s} ${-34 * s}`} /><P d={`M${x} ${y} q ${-1 * s} ${-24 * s} ${2 * s} ${-42 * s}`} /><P d={`M${x} ${y} q ${8 * s} ${-18 * s} ${18 * s} ${-30 * s}`} /><P d={`M${x} ${y} q ${4 * s} ${-26 * s} ${-6 * s} ${-38 * s}`} /><P d={`M${x} ${y} q ${12 * s} ${-10 * s} ${26 * s} ${-16 * s}`} />
  </g>;
}
/** 城台的垛口：从 x1 到 x2 沿 y 画一排齿。 */
export function crenel(x1: number, x2: number, y: number, h = 8, w = 12, gap = 10): string {
  let d = `M${x1} ${y + h}`;
  for (let x = x1; x + w <= x2 + 0.1; x += w + gap) d += ` L${x} ${y + h} L${x} ${y} L${x + w} ${y} L${x + w} ${y + h}`;
  return d + ` L${x2} ${y + h}`;
}
/** 沙丘：迎风坡缓、背风坡陡；返回脊线采样点。 */
function dune(xc: number, width: number, base: number, height: number): Point[] {
  const x0 = xc - 0.68 * width, x1 = xc + 0.32 * width;
  const points: Point[] = [];
  for (let x = x0; x <= x1 + 0.01; x += 10) {
    const t = x <= xc ? (() => { const u = (x - x0) / (xc - x0); return u * u * (3 - 2 * u); })() : 1 - Math.pow((x - xc) / (x1 - xc), 0.62);
    points.push([x, base - height * t]);
  }
  return points;
}
/** 背风坡的排线：从脊线下方起笔，陡陡地向下扫，像沙在滑落。 */
function leeHatch(points: readonly Point[], xc: number, count: number): string[] {
  const lee = points.filter(([x]) => x > xc + 6);
  const step = Math.max(1, Math.floor(lee.length / (count + 1)));
  return lee.filter((_, index) => index % step === 0 && index > 0).slice(0, count).map(([x, y], index) => {
    const length = 26 - index * 1.6;
    return `M${f(x)} ${f(y + 6)} l ${f(length * 0.32)} ${f(length)}`;
  });
}
const CLUSTER: readonly Point[] = [[0, 0], [11, -7], [-10, -8], [8, 10], [-11, 7], [1, -16], [14, 4]];
function ElmSeeds({ at }: { at: readonly Point[] }): JSX.Element {
  return <g fill={LOESS} fillOpacity=".42" stroke={LOESS} strokeWidth="1">{at.flatMap(([cx, cy]) => CLUSTER.map(([dx, dy]) => <circle key={`${cx}-${cy}-${dx}-${dy}`} cx={cx + dx} cy={cy + dy} r={5} />))}</g>;
}
const leaf = (x: number, y: number, angle: number) => <g key={`${x}-${y}`} transform={`rotate(${angle} ${x + 24} ${y})`}><P d={`M${x} ${y} c 12 -16 36 -16 48 0 c -12 16 -36 16 -48 0 z`} /><P d={`M${x + 4} ${y} h 40`} strokeWidth="1" opacity=".7" /></g>;

function Spring(): JSX.Element {
  return <>
    <Sun cx={1190} cy={112} r={46} />
    <Strata />
    <g fill="none" stroke={LOESS} strokeWidth="1" opacity=".7">
      {([[1120, 60], [1178, 76], [1246, 66], [1306, 82], [1368, 62], [1420, 72]] as const).map(([x, h]) => <g key={x}><P d={`M${x} 354 v ${-h * 0.45}`} /><P d={`M${x - h * 0.38} ${354 - h * 0.45} c ${-h * 0.05} ${-h * 0.7} ${h * 0.8} ${-h * 0.7} ${h * 0.76} 0`} /></g>)}
    </g>
    <g fill="none" stroke={INK} strokeWidth="1.6" strokeLinecap="round">
      <P d="M-30 64 C 180 98 330 118 450 170 S 650 254 730 240" />
      <P d="M200 94 C 222 72 240 62 272 58" /><P d="M300 118 C 328 90 358 76 412 72" /><P d="M382 150 C 402 128 424 118 474 112" /><P d="M462 176 C 472 202 492 216 526 222" /><P d="M522 198 C 544 178 566 168 616 162" /><P d="M624 226 C 654 208 676 198 706 198" /><P d="M560 206 C 576 232 596 244 630 250" />
      {leaf(430, 92, -30)}{leaf(484, 128, 15)}{leaf(236, 110, 20)}{leaf(640, 176, -20)}{leaf(690, 232, 10)}
    </g>
    <ElmSeeds at={[[272, 58], [412, 72], [474, 112], [526, 222], [616, 162], [706, 198], [630, 250], [340, 104]]} />
    <g fill="none" stroke={INK} strokeWidth="1.2" strokeLinecap="round" opacity=".8"><P d="M900 128 q 9 -11 18 0 q 9 -11 18 0" /><P d="M962 158 q 8 -10 16 0 q 8 -10 16 0" /></g>
    <Tuft x={90} y={462} size={1.1} /><Tuft x={150} y={468} size={.8} /><Tuft x={1440} y={466} size={1} /><Tuft x={1500} y={470} size={.7} />
  </>;
}

function Summer(): JSX.Element {
  // 四层城台，墙面略有收分；长城从台右沿坡下行，一级一级。
  const tiers = [[750, 1050, 350, 400, 8], [790, 1010, 306, 350, 7], [826, 974, 266, 306, 6], [854, 946, 232, 266, 5]] as const;
  const steps = Array.from({ length: 6 }, (_, i) => ({ x1: 1050 + i * 96, x2: 1146 + i * 96, y: 400 + i * 7 }));
  return <>
    <Sun cx={1200} cy={104} r={48} />
    <g fill="none" stroke={INK} strokeWidth="1.3" strokeLinecap="round" opacity=".9">
      <P d="M220 152 c 20 -36 66 -36 86 0 c 16 -30 62 -30 78 0 c 13 -20 46 -20 60 0" /><P d="M248 152 h 176" opacity=".55" />
      <P d="M330 206 c 16 -30 56 -30 72 0 c 13 -23 50 -23 63 0 c 10 -16 36 -16 46 0" /><P d="M356 206 h 150" opacity=".55" />
      <P d="M580 176 q 7 -8 14 0 q 7 -8 14 0" /><P d="M624 160 q 6 -7 12 0 q 6 -7 12 0" /><P d="M604 196 q 6 -7 12 0 q 6 -7 12 0" />
    </g>
    <Strata />
    <P d="M700 400 H 1100" stroke={INK} strokeWidth="1.3" />
    <g fill={PAPER} stroke={INK} strokeWidth="1.4" strokeLinejoin="round">
      {tiers.map(([x1, x2, y1, y2, batter]) => <P key={x1} d={`M${x1} ${y2} L${x1 + batter} ${y1 + 8} L${x2 - batter} ${y1 + 8} L${x2} ${y2} Z`} />)}
      {tiers.map(([x1, x2, y1, , batter]) => <P key={`c${x1}`} d={crenel(x1 + batter, x2 - batter, y1, 8, 13, 10)} />)}
      <P d="M846 232 Q 900 216 954 232 L 940 232 Q 900 224 860 232 Z" /><P d="M866 232 L 872 214 L 928 214 L 934 232" /><P d="M880 214 Q 900 206 920 214" fill="none" />
      <P d="M886 400 v -22 a 14 14 0 0 1 28 0 v 22" fill="none" />
    </g>
    <g fill={PAPER} stroke={INK} strokeWidth="1.2" strokeLinejoin="round" opacity=".9">
      {steps.map(({ x1, x2, y }) => <g key={x1}><P d={`M${x1} ${y} L${x1} ${y - 26} L${x2} ${y - 26} L${x2} ${y} Z`} /><P d={crenel(x1, x2, y - 34, 7, 11, 9)} /></g>)}
    </g>
    <g fill="none" stroke={INK} strokeWidth="1" strokeLinecap="round" opacity=".7"><P d="M120 466 c 10 -16 36 -16 46 0" /><P d="M172 470 c 8 -12 28 -12 36 0" /></g>
    <Tuft x={80} y={462} size={1} /><Tuft x={230} y={468} size={.8} /><Tuft x={1540} y={470} size={.8} />
  </>;
}

function Autumn(): JSX.Element {
  const far: Point[] = [[860, 338], ...dune(1150, 520, 338, 52), [1460, 338]];
  const left: Point[] = [[-30, 424], ...dune(420, 780, 424, 134)];
  const right: Point[] = [...dune(1250, 660, 446, 104), [1630, 446]];
  const geese: Point[] = [[520, 108], [484, 126], [448, 144], [412, 162], [376, 180], [556, 122], [592, 136], [628, 150], [664, 164]];
  return <>
    <Sun cx={1200} cy={110} r={48} />
    <g data-drift="geese" fill="none" stroke={INK} strokeWidth="1.4" strokeLinecap="round">{geese.map(([x, y]) => <P key={`${x}-${y}`} d={`M${x} ${y} q 9 -9 18 0 q 9 -9 18 0`} />)}</g>
    <Strata from={3} />
    <g strokeLinecap="round">
      <P d={massBelow(far)} fill={PAPER} /><P d={smooth(far)} fill="none" stroke={LOESS} strokeWidth="1.1" />
      <P d={massBelow(left)} fill={PAPER} /><P d={smooth(left)} fill="none" stroke={INK} strokeWidth="1.5" />
      <P d={massBelow(right)} fill={PAPER} /><P d={smooth(right)} fill="none" stroke={INK} strokeWidth="1.5" />
      <g fill="none" stroke={INK} strokeWidth=".9" opacity=".65">{[...leeHatch(left, 420, 11), ...leeHatch(right, 1250, 9), ...leeHatch(far, 1150, 5)].map(d => <P key={d} d={d} />)}</g>
      <P d={smooth([[-30, 466], [300, 458], [620, 464], [980, 456], [1300, 462], [1630, 458]])} fill="none" stroke={LOESS} strokeWidth="1" opacity=".6" />
    </g>
    <Tuft x={1500} y={468} size={1.3} /><Tuft x={1548} y={472} size={.9} /><Tuft x={60} y={470} size={.9} />
  </>;
}

function Winter(): JSX.Element {
  const caves = [700, 800, 900, 1000];
  const slope: Point[] = [[560, 400], [760, 362], [980, 322], [1240, 300], [1630, 282]];
  const snow: Point[] = [[40, 30], [96, 70], [150, 24], [210, 60], [290, 36], [330, 90], [380, 50], [440, 22], [490, 72], [560, 40], [610, 110], [680, 30], [720, 86], [760, 52], [120, 120], [260, 130], [420, 120], [520, 128], [700, 140], [60, 160], [340, 158], [600, 60], [180, 90], [470, 100], [740, 120], [20, 100], [640, 150], [230, 180], [560, 170], [90, 210], [860, 40], [920, 96], [1010, 60], [1080, 130], [1150, 40], [1210, 90], [1290, 54], [1350, 128], [1440, 70], [1500, 30], [1560, 110], [1380, 190], [1120, 190], [980, 170], [1470, 160], [1580, 200], [820, 170], [1250, 160], [1530, 240], [1050, 232]];
  return <>
    <Sun cx={1190} cy={92} r={36} opacity={0.35} />
    <g fill={INK} opacity=".42">{snow.map(([x, y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r={1.8} />)}</g>
    <Strata />
    <P d={massBelow(slope)} fill={PAPER} /><P d={smooth(slope)} fill="none" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
    <g fill="none" stroke={INK} strokeWidth="1.1" strokeLinecap="round" opacity=".75">
      {([[1260, 300, 52], [1340, 292, 44], [1430, 286, 58], [1500, 284, 40]] as const).map(([x, y, h]) => <g key={x}><P d={`M${x} ${y} v ${-h}`} /><P d={`M${x} ${y - h * 0.55} l ${-h * 0.32} ${-h * 0.3}`} /><P d={`M${x} ${y - h * 0.7} l ${h * 0.3} ${-h * 0.28}`} /><P d={`M${x} ${y - h * 0.4} l ${h * 0.22} ${-h * 0.2}`} /></g>)}
    </g>
    <P d="M560 400 H 1180" stroke={INK} strokeWidth="1.3" />
    <g fill={PAPER} stroke={INK} strokeWidth="1.5" strokeLinejoin="round">
      {caves.map(x => <g key={x}><P d={`M${x} 400 v -34 a 34 34 0 0 1 68 0 v 34 z`} /><P d={`M${x + 23} 400 v -66 M${x + 45} 400 v -66 M${x} 372 h 68 M${x} 386 h 68`} strokeWidth=".9" opacity=".7" fill="none" /></g>)}
    </g>
    <P d="M1100 330 h 14 v -12 h -14 z" fill={PAPER} stroke={INK} strokeWidth="1.2" />
    <g fill="none" stroke={INK} strokeWidth="1.5" strokeLinecap="round">
      <P d="M380 420 C 383 350 378 290 390 238" /><P d="M386 316 c -32 -22 -48 -48 -64 -70" /><P d="M385 284 c 26 -16 42 -42 48 -68" /><P d="M388 340 c -22 -10 -36 -28 -42 -48" /><P d="M390 238 c 12 -20 22 -36 26 -54" /><P d="M354 266 c -12 -12 -20 -26 -22 -42" /><P d="M402 262 c 14 -8 24 -20 30 -34" />
      <P data-drift="smoke" d="M1107 318 c -12 -22 12 -34 0 -56 c -10 -16 6 -26 2 -42" opacity=".55" />
    </g>
    <g fill="none" stroke={INK} strokeWidth="1.1" strokeLinecap="round" opacity=".8">
      {[90, 140, 190, 240, 290, 340].map(x => <P key={x} d={`M${x} 470 v -34`} />)}<P d="M78 448 L 352 442" /><P d="M78 462 L 352 456" />
    </g>
  </>;
}

const SCENES: Record<Season, () => JSX.Element> = { spring: Spring, summer: Summer, autumn: Autumn, winter: Winter };
export function SeasonArt({ season, className = "" }: { season: Season; className?: string }): JSX.Element {
  const Scene = SCENES[season];
  return <svg className={`season-art ${className}`} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMax slice" aria-hidden="true" focusable="false" data-season={season}><g data-layer="motif"><Scene /></g></svg>;
}
